import api from "../services/authService";

/**
 * Graph Explorer API client.
 *
 * Wraps the three admin-only endpoints exposed by `doc_pipeline.graph.views`
 * (backend PR #554 merged 2026-05-15 ee73b69d). All calls flow through the
 * shared `authService` axios instance for JWT + CSRF + 401-refresh. Backend
 * gate is `IsRoleAdmin`, which accepts both `admin` and `super_admin`.
 *
 * Surface:
 *   - listGraphRuns         GET  /api/pipeline/graph-runs/
 *   - getGraphRun           GET  /api/pipeline/graph-runs/<id>/
 *   - getMatterGraphSummary GET  /api/pipeline/matters/<matter_id>/graph-summary/
 *
 * Conventions match service/inbox.js + service/analysis.js:
 *   - Functions return the raw axios response so callers can inspect status
 *     when useful.
 *   - DRF PageNumberPagination envelopes are returned verbatim
 *     (`{ count, next, previous, results }`) for the list endpoint.
 *   - Numeric ids are coerced to strings via `String(...)` for the URL — both
 *     the backend BIGINT columns and the JS Number type round-trip cleanly
 *     for the actual id space we use, but explicit coercion keeps malformed
 *     callers from sending `[object Object]` into the URL.
 */

/**
 * GET /api/pipeline/graph-runs/?matter_id=<int>&document_id=<int>&page=<int>&page_size=<int>
 *
 * Returns a DRF-paginated list of `GraphExtractionRun` rows newest-first by
 * `created_at`. Each row carries summary counts only (raw / kept / dropped
 * for entities and facts) plus the GCS artifact URIs — drill into the detail
 * endpoint for the inline dropped-record sample.
 *
 * Defaults match the BE pagination contract: page_size 25, max 200.
 */
export const listGraphRuns = ({
  matterId,
  documentId,
  page = 1,
  pageSize = 25,
} = {}) => {
  const params = new URLSearchParams();
  if (matterId != null && matterId !== "") {
    params.set("matter_id", String(matterId));
  }
  if (documentId != null && documentId !== "") {
    params.set("document_id", String(documentId));
  }
  if (page && page !== 1) params.set("page", String(page));
  if (pageSize && pageSize !== 25) params.set("page_size", String(pageSize));
  const qs = params.toString();
  const url = qs ? `/pipeline/graph-runs/?${qs}` : `/pipeline/graph-runs/`;
  return api.get(url);
};

/**
 * GET /api/pipeline/graph-runs/<run_id>/
 *
 * Returns a single `GraphExtractionRun` with full inline detail — the
 * dropped-record sample (entities + facts, each capped at 200 in PR #554)
 * comes back in `dropped_entities` / `dropped_facts`.
 */
export const getGraphRun = (runId) => {
  if (runId == null || runId === "") {
    return Promise.reject(new Error("runId required"));
  }
  return api.get(`/pipeline/graph-runs/${encodeURIComponent(String(runId))}/`);
};

/**
 * GET /api/pipeline/matters/<matter_id>/graph-summary/
 *
 * One-shot rollup across every `GraphExtractionRun` for a matter. Returns
 * aggregated raw/kept/dropped totals plus the top-10 most-dropped entity
 * types and fact predicates (`row_count` is rows-that-saw-the-type, not
 * total record count). Empty matters return zeros + empty lists rather
 * than 404 so the dashboard renders cleanly before any docs have been
 * processed.
 */
export const getMatterGraphSummary = (matterId) => {
  if (matterId == null || matterId === "") {
    return Promise.reject(new Error("matterId required"));
  }
  return api.get(
    `/pipeline/matters/${encodeURIComponent(String(matterId))}/graph-summary/`
  );
};

/**
 * GET /api/pipeline/matters/<matter_id>/graph-inspector/[?limit=N]
 *
 * Returns read-only Oxigraph entity rows grouped into table tabs (Conditions,
 * Determinations, Evidence, Medical facts, Employment, TDIU/SMC). This is
 * distinct from Graph Explorer's extraction-audit view.
 *
 * `limit` is optional; when omitted the backend returns every row for the
 * matter (the inspector is meant for full review, not sampling).
 */
export const getMatterGraphInspector = (matterId, { limit } = {}) => {
  if (matterId == null || matterId === "") {
    return Promise.reject(new Error("matterId required"));
  }
  const params = new URLSearchParams();
  if (limit != null && limit !== "") params.set("limit", String(limit));
  const qs = params.toString();
  return api.get(
    `/pipeline/matters/${encodeURIComponent(String(matterId))}/graph-inspector/${
      qs ? `?${qs}` : ""
    }`
  );
};
